/* Reset Password — public, consumes the token from the email link. */
import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, KeyRound, ArrowLeft, CheckCircle2 } from "lucide-react";
import PasswordRulesHint from "@/components/PasswordRulesHint";
import { validateNewPassword } from "@/lib/password";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);

  const [form, setForm] = useState({ next: "", confirm: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("This reset link is missing its token. Please use the link from your email."); return; }
    const v = validateNewPassword(form.next, form.confirm);
    if (v) { setErr(v); return; }
    setSubmitting(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: form.next });
      setDone(true);
      setTimeout(() => navigate("/", { replace: true }), 2200);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not reset password.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-12" data-testid="reset-password-page">
      <div className="mx-auto max-w-md">
        <Link to="/" className="mb-6 inline-flex items-center text-sm text-slate-600 underline-offset-2 hover:underline" data-testid="rp-back-link">
          <ArrowLeft className="mr-1 size-4" /> Back to sign in
        </Link>
        <Card className="border-slate-200 p-6">
          <div className="flex items-start gap-3">
            <div className="rounded-lg bg-teal-50 p-2.5 text-teal-700"><KeyRound className="size-5" /></div>
            <div>
              <h1 className="font-heading text-xl font-black tracking-tight text-slate-900">Set a new password</h1>
              <p className="mt-1 text-sm text-slate-600">
                {token ? "Choose a new password to sign in to FlowQA." : "This reset link is missing its token — open the link directly from your email."}
              </p>
            </div>
          </div>

          {done ? (
            <div className="mt-6 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="rp-done">
              <CheckCircle2 className="mb-2 inline-block size-5" />
              <div className="font-semibold">Password reset.</div>
              <p className="mt-1">You can now sign in with your new password. Redirecting…</p>
            </div>
          ) : (
            <form className="mt-6 space-y-4" onSubmit={submit}>
              <div>
                <Label>New password</Label>
                <Input
                  type="password" autoComplete="new-password"
                  value={form.next}
                  onChange={(e) => setForm({ ...form, next: e.target.value })}
                  data-testid="rp-new-input"
                  required
                  disabled={!token}
                />
              </div>
              <div>
                <Label>Confirm new password</Label>
                <Input
                  type="password" autoComplete="new-password"
                  value={form.confirm}
                  onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                  data-testid="rp-confirm-input"
                  required
                  disabled={!token}
                />
              </div>

              <PasswordRulesHint />

              {err && (
                <div className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-900" data-testid="rp-error">{err}</div>
              )}

              <Button type="submit" disabled={submitting || !token} className="w-full bg-teal-700 hover:bg-teal-800" data-testid="rp-submit-btn">
                {submitting ? (<><Loader2 className="mr-2 size-4 animate-spin" /> Resetting…</>) : "Reset password"}
              </Button>
            </form>
          )}
        </Card>
      </div>
    </div>
  );
}
